import { Directive, ElementRef, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AppTableEvent } from '../model/table/appTableEvent.model';
import { AppTableOptions } from '../model/table/appTableOptions.model';
import { TableLanguageService } from '../service/table-language.service';
import { ClipboardService } from '../service/clipboard.service';
import { AppTableClipboardValidation } from '../model/table/appTableClipboardValidation.model';
import { TranslateService } from '@ngx-translate/core';
import { AppAbstractDirective } from './appAbstractDirective';
import { EntityInterface } from '../model/entity/interfaces/entityInterface.model';
import { AuthService } from '@app/core/auth/auth.service';
import { EntityServiceInterface } from '../service/interfaces/EntityServiceInterface';

@Directive({
  selector: '[appTable]'
})
export class AppTableDirective extends AppAbstractDirective implements OnInit {

  // ********************** Propiedades de la directiva ********************

  @Input() fixed: string;               // si la tabla es fija o no
  @Input() clickOnSearch: string;       // click si la busqueda devuleve 1
  @Input() copyEnabled: string;         // si se permite copiar
  @Input() pasteEnabled: string;        // si se permite pegar
  @Input() appTableOptions: AppTableOptions;  // opciones de la tabla
  @Input() delete: string;              // si el borrado es con un botón dentro de la tabla
  @Input() edit: string;                // si la edición es con un botón dentro de la tabla
  @Input() writePermission: string;     // si tiene permiso de escritura
  @Input() responsive: string;          // si la tabla es responsive o no
  @Input() entityName: string;          // si la tabla es responsive o no
  @Input() buttonsColIndex: string;     // Indice de la columna donde pintar los botones de edición/borrado
  

  // ********************** Metodos de componente padre ********************

  @Output() appClickMethod = new EventEmitter<any>();       // click
  @Output() appDblClickMethod = new EventEmitter<any>();    // doble click
  @Output() appEnterMethod = new EventEmitter<any>();       // enter
  @Output() appShiftEnterMethod = new EventEmitter<any>();  // alt enter
  @Output() appPasteMethod = new EventEmitter();       // validar paste
  @Output() appDeleteMethod = new EventEmitter<any>();      // metodo del componente para borrar uun registro
  @Output() appEditMethod = new EventEmitter<any>();        // metodo del componente para editar uun registro
  @Output() appActionMethod = new EventEmitter<any>();      // metodo del componente para llamar a acciones en general

  // ********************** propiedades de ayuda ***************************

  // si ya se ha cargado la tabla
  public isInitialize = false;
  // si ya se ha cargado la tabla
  public serverSide = false;

  // propiedad con la altura del primer registro de la tabla para el cálculo
  // de registros por página
  private rowHeight: number;
  // propiedad con la altura de la paginación de la tabla para el cálculo
  // de registros por página
  private pageControllerHeight: number;
  // propiedad con la altura del titulo de la tabla para el cálculo
  // de registros por página
  private headHeight: number;
  // altura del div contenedor de la tabla
  private parentDivHeight: number;
  // registros por página
  private rowPerPage: number;
  // propiedad booleana para ver si la altura es fija o no
  private isFixed: boolean;
  // propiedad booleana para ver si la altura es fija o no
  private isAngular: boolean;
  // propiedad para ver si la tabla tiene Scroll o no
  private isScroll: boolean;
  // propiedad para ver si la tabla tiene teclas o no
  private isKeys: boolean;
  // propiedad para ver si la tabla permite copiar
  private isCopyEnable: boolean;
  // propiedad para ver si la tabla permite pegar
  private isPasteEnable: boolean;
  // propiedad para ver si cuando se realiza una busqueda y solo hay un solo
  // div contenedor padre de la tabla
  private parentElement: any;
  // el DataTable
  private table: any;
  // testigo para deshabilitar el click si ya se ha pulsado un enter
  private disabledClick: boolean;
  // testigo para deshabilitar el click en el evento de busqueda si ya ha ocurrido
  private disabledClickOnSearch: boolean;
  // testigo para comprobar si se ha cambiado el tamaño por primera vez
  private isFirstResize: boolean;
  // propiedad para el campo de busqueda
  private searchTableInput: ElementRef;
  // si se debe de contemplar o no la busqueda
  private isSearchTableInput: boolean;
  // para determinar si tiene borrado o no
  private isDelete: boolean;
  // para determinar si tiene edición o no
  private isEdit: boolean;
  // para determinar si se tiene permisso de escritura
  private isWritePermission: boolean;
  // para determinar si se tiene permisso de escritura
  private isResponsive: boolean;
  // almacena el indice del último registro seleccionado
  private lastSelectedIndex: number = null;

  // ********************** Constructor ***********************************

  constructor(
    // elemento tabla de la directiva
    // tslint:disable-next-line: no-shadowed-variable
    private element: ElementRef,
    // servicio para el redimensionado del div contenedor
    // private resizeService: ResizeService,
    // servicio de lenguaje de la tabla
    private cronossTableLanguage: TableLanguageService,
    // servicio de copiar y pegar
    private appClipboard: ClipboardService,
    // servicio de traducciones
    private traslate: TranslateService,
    private authService: AuthService
  ) {
    super();
  }

  // ********************** Metdodos y funciones de API **********************

  public jqueryTableUpdateEntity(entity: EntityInterface, rowIndex: number, draw: boolean = true): void {
    try {
      this.table.row(rowIndex).data(entity);
      if (!this.serverSide && draw) {
        this.table.draw(false);
      }
    } catch (error) {
      throw new Error('app.dt.jqueryTableAddEntity.error');
    }
  }

  public jqueryTableAddEntity(entity: EntityInterface, draw = true): void {
    try {
      this.table.row.add(entity);
      if (draw) {
        this.table.draw();
      }
    } catch (error) {
      throw new Error('app.dt.jqueryTableAddEntity.error');
    }
  }

  public jqueryTableAddEntities(entities: Array<EntityInterface>, draw: boolean = true): void {
    try {
      entities.forEach((entity) => {
        this.jqueryTableAddEntity(entity, false);
      });
      if (draw) {
        this.table.draw();
      }
    } catch (error) {
      throw new Error('app.dt.jqueryTableAddEntities.error');
    }
  }

  // -------------------------------------------------------------------------
  // función para inicializar la tabla para el modo jquery
  public jqueryDataTableInitial(searchTableInput?: ElementRef): void {

    this.isAngular = false;

    if (searchTableInput !== undefined && searchTableInput !== null) {
      this.searchTableInput = searchTableInput;
      this.isSearchTableInput = true;
    } else {
      this.isSearchTableInput = true;
    }

    // capturamos el elemento de la tabla
    const tableElement = this.element.nativeElement;
    // comprobamos que exista el elemento
    if (tableElement) {
      // si existe

      this.setDatatable();

      // establecemos la carga de listener
      this.setListeners();

      // // pregintamos si la altura de la tabla no es fija
      // if (!this.isFixed) {
      //   this.setDataResize();
      //   this.resize(true);
      // }

    }
  }

  public destroy(): void {
    this.table.destroy(false);
  }

  // -------------------------------------------------------------------------
  // función para inicializar la tabla para el modo jquery
  public reInit(): void {
    // this.appTableOptions.destroy = true;
    this.table = $(this.element.nativeElement).DataTable(
      this.appTableOptions
    );
  }

  // -------------------------------------------------------------------------
  // función para inicializar la tabla cuando es por angular
  angularDataTableInitial(searchTableInput: ElementRef): void {

    this.isAngular = true;

    if (searchTableInput !== undefined && searchTableInput !== null) {
      this.searchTableInput = searchTableInput;
      this.isSearchTableInput = true;
    } else {
      this.isSearchTableInput = true;
    }

    // capturamos el elemento de la tabla
    const tableElement = this.element.nativeElement;
    // comprobamos que exista el elemento
    if (tableElement) {
      // si existe

      this.appTableOptions.responsive = true;

      this.setDatatable();

      this.setListeners();

      // // pregintamos si la altura de la tabla noñecemos los valores de la tabla
      // if (!this.isFixed) {
      //   // si no es fija
      //   // estab
      //   this.setDataResize();

      //   // redimensionamos la tabla, al principio
      //   this.resize(true);

      // }
    }
  }

  // -------------------------------------------------------------------------
  // método para cambiar los datos de una tabla a partir del array de elementos que se envía
  public jqueryDataTableChange(entities: Array<EntityInterface>): void {
    this.table.clear();
    this.table.rows.add(entities);
    this.table.draw();
    // if (!this.isFixed) {
    //   if (!this.isFirstResize) {
    //     this.resize(true);
    //   }
    // }
  }

  // -------------------------------------------------------------------------
  // método para borrar un registro
  public dataTableRemoveRow(rowIndex: number, draw: boolean = true): void {
    // this.table.clear();
    this.table.row(rowIndex).remove();
    if (draw) {
      this.table.draw();
    }
  }


  // -------------------------------------------------------------------------
  // método para cambiar borrar varios registros
  public dataTableRemoveRows(rowIndex: number, rows: number, draw: boolean = true): void {
    for (let i = rowIndex; i <= rowIndex + rows; i++) {
      this.dataTableRemoveRow(rowIndex, false);
    }
    if (draw) {
      this.table.draw();
    }
  }

  // método para cambiar borrar varios registros
  public getAllRows(): Array<EntityInterface> {
    return this.table.rows().data();
  }

// -------------------------------------------------------------------------
  // devuelve un array de elementos que tiene la tabla
  public getAllElements(): Array<EntityInterface> {

    const elements: Array<EntityInterface> = [];

    const elementsdRows = this.table.rows();
    elementsdRows.every(( index: number) => {
      const row = this.table.row(index);
      let entity: EntityInterface;
      if (this.isAngular) {
        entity = JSON.parse(row.data()[0]);
      } else {
        entity = row.data();
      }
      elements.push(entity);
    });
    return elements;
  }

  getRowsCount(): number {
    return this.table.rows().count() as number;
  }

  // -------------------------------------------------------------------------
  // devuelve un array de elementos con los que tiene seleccionados la tabla
  public getSelectedEntities(): Array<EntityInterface> {

    const elements: Array<EntityInterface> = [];

    const selectedRows = this.table.rows( { selected: true } );
    selectedRows.every(( index: number) => {
      const row = this.table.row(index);
      let entity: EntityInterface;
      if (this.isAngular) {
        entity = JSON.parse(row.data()[0]);
      } else {
        entity = row.data();
      }
      elements.push(entity);
    });
    return elements;
  }

  // -------------------------------------------------------------------------
  // devuelve el elemtno seleccionado, si hay más de uno devuelve el primero
  public getSelectedEntity(): EntityInterface {
    if (this.getSelectedEntityCount() > 0) {
      return this.getSelectedEntities()[0];
    } else {
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // devuelve un el número de elementos seleccionados
  public getSelectedEntityCount(): number {
    return this.table.rows( { selected: true } ).count() as number;
  }

  // -------------------------------------------------------------------------
  // copia a localStorage los elementos seleccionados
  public copySelectedRows(): boolean {
    if (this.getSelectedEntityCount() > 0) {
      const selectedRowsData = this.getSelectedEntities();
      let entityName = this.entityName;
      if (!entityName) {
        entityName = 'GENERIC';
      }
      this.appClipboard.copy(entityName, selectedRowsData);
    } else {
      return false;
    }
  }

  // -------------------------------------------------------------------------
  // pega desde localStorage los elementos copiados
  public paste(): boolean {
    if (this.isWritePermission) {
      if (this.isMethodEnabled(this.appPasteMethod)) {
        this.appPasteMethod.emit();
        return true;
      }
    }
  }

  // -------------------------------------------------------------------------
  // metodo para llamar desde el padre cuando se produce una validación del paste
  public returnPasteValidation(appDtClipboarValidation: AppTableClipboardValidation): void {
    if (appDtClipboarValidation.validate) {
      try {
        this.jqueryTableAddEntities(appDtClipboarValidation.entities);
      } catch (error) {
        console.log(error);
        throw new Error('app.dt.returnPasteValidation.error');
      }
    } else {
      console.log('Table paste not available');
    }
  }

  // -------------------------------------------------------------------------
  // busca por una columna
  public columnSearch(colIndex: number, valueSearch: string, match: boolean = false): void {
    if(!match) {
      this.table.column(colIndex).search(valueSearch);
    } else {
      this.table.column(colIndex).search('^' + valueSearch  + '$', true, false);
    }
  }

  // -------------------------------------------------------------------------
  // oculta una columna
  public hideColumn(colIndex: number): void {
    this.table.column(colIndex).visible(false);
  }

  // -------------------------------------------------------------------------
  // muestra una columna
  public showColumn(colIndex: number): void {
    this.table.column(colIndex).visible(true);
  }

  // -------------------------------------------------------------------------
  // repinta la tabla
  public draw(): void {
    this.table.draw();
  }

  // -------------------------------------------------------------------------
  // repinta la tabla
  public resizeColumns(timeOut?: number): void {
    if (timeOut) {
      setTimeout(() => {
        this.table.columns.adjust().draw();
      }, timeOut);
    } else {
      this.table.columns.adjust().draw();
    }
    this.table.draw();
  }

  // --------------------------------------------------------------------------------
  // para buscar el registro
  findEntityOnTable(entity: EntityInterface, entityService: EntityServiceInterface): void {
    const row = this.table.row((index: number, entityToFind: EntityInterface) => entityService.getURLIdFromEntity(entity) === entityService.getURLIdFromEntity(entityToFind));
    if (row.length > 0) {
      this.table.rows().deselect();
      row.select().show();
      if (!this.serverSide) {
        this.table.draw(false);
      }
    }
  }

  getIndex(entity: EntityInterface, entityService: EntityServiceInterface): number {
    const row = this.table.row((index: number, entityToFind: EntityInterface) => entityService.getURLIdFromEntity(entity) === entityService.getURLIdFromEntity(entityToFind));
    if (row) {
      return row.index();
    }
    return null;
  }

  // --------------------------------------------------------------------------------
  // para buscar el registro
  goToRow(index: number): void {
    if (!this.isScroll) {
      this.table.row(index).show();
      this.table.draw(false);
    } else {
      this.table.row(index).scrollTo();
    }
  }

  // --------------------------------------------------------------------------------
  // para buscar el registro
  goToLastPage(): void {
    this.table.page('last').draw('page');
  }

  clear(): void {
    this.table.clear();
  }

  public search(searchValue: string): void {
    this.table.search(searchValue);
    this.table.draw();
  }


  // ********************** Metdodos de ciclo de vida ********************

  // inicio de la directiva
  ngOnInit(): void {

    // inicializamos las propiedades
    this.parentDivHeight = 0;
    this.rowPerPage = 0;
    this.rowHeight = 0;
    this.pageControllerHeight = 0;
    this.headHeight = 0;
    this.isScroll = false;
    this.isKeys = false;
    this.isFixed = true;
    this.isCopyEnable = (this.copyEnabled === 'true' ? true : false);
    this.isPasteEnable = (this.pasteEnabled === 'true' ? true : false);
    // this.isClickOnSearch = (this.clickOnSearch === 'true' ? true : false);
    this.isDelete = (this.delete === 'false' ? false : true);
    this.isEdit = (this.edit === 'false' ? false : true);
    this.isWritePermission = (this.writePermission === 'true' ? true : false);
    this.isResponsive = (this.responsive === 'false' ? false : true);
    this.disabledClick = false;
    this.disabledClickOnSearch = false;
    this.isFirstResize = false;

    // comprueba si la tabla es fija
    if (!this.isFixed) {
      // inicializo el elemento padre que es el div contenedor
      this.parentElement = this.element.nativeElement.parentElement;
    }

  }

  // ********************** Metdodos y funciones internas ********************

  private setDatatable() {

    const language = this.cronossTableLanguage.getLanguageTexts();

    // le añado los valores de la tabla por defecto que van a tener todos
    this.appTableOptions.responsive = {
      details: false
    };
    if (language !== null) {
      this.appTableOptions.language = language;
    }
    this.appTableOptions.processing = true;
    this.appTableOptions.dom = 'rt<"row"<"col-sm-4"i><"col-sm-8"p>>';
    if (!this.isAngular) {
      this.appTableOptions.data = [];
    }

    // inicializamos la tabla
    this.setButtons(this.appTableOptions);
    if (this.appTableOptions.serverSide) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const that = this;
      this.appTableOptions.ajax.beforeSend = (request: any) => {
        request.setRequestHeader('Authorization', `Bearer ${AuthService.getToken()}`);
        request.then((t: any, n: any, requestVariable: any) => {
          if (requestVariable.getResponseHeader('Renew') != null) {
            that.authService.setToken(requestVariable.getResponseHeader('Authorization'));
          }
        });
      };
    }

    // comprobamos si es serverSide
    if (this.appTableOptions.ajax) {
      this.serverSide = true;
    }

    this.table = $(this.element.nativeElement).DataTable(
      this.appTableOptions
    );

    // inicializamos la información de si la tabla es con scroll o no
    this.isScroll = this.table.init().scrollCollapse;
    // inicializamos la información de si la tabla es con scroll o no
    this.isKeys = this.table.init().keys;
    // lo pongo como cargado
    this.isInitialize = true;

  }

  // -------------------------------------------------------------------------
  // función para incluir el botón o icono de borrado
  private setButtons(appTableOptionsAux: any) {
    
    // Si no se ha establecido el indice de la columna de los botones, se deduce: el último
    const colIndex = this.buttonsColIndex != null ? parseInt(this.buttonsColIndex) : this.element.nativeElement.tHead.rows[this.element.nativeElement.tHead.rows.length - 1].cells.length - 1;

    const deleteText = this.traslate.instant('common.delete') as string;
    const editText = this.traslate.instant('common.edit') as string;

    // establezco el boton de borrar o el icono
    let buttonDelete = '';

    let buttonDetail: string;
    if (this.isWritePermission) {
      buttonDelete = `<button type="button" class="APP-ACTION ${AppTableEvent.EVENT_REMOVE} btn btn-primary btn-table" title="${deleteText}"><i class="far fa-trash-alt"></i></button>`;
      buttonDetail = `<button type="button" class="APP-ACTION ${AppTableEvent.EVENT_EDIT} btn btn-primary btn-table" title="${editText}"><i class="fas fa-pen"></i></button>`;
    } else {
      buttonDetail = `<button type="button" class="APP-ACTION ${AppTableEvent.EVENT_EDIT} btn btn-primary btn-table" title="${editText}"><i class="fas fa-info"></i></button>`;
    }

    let buttons = '';
    if (this.isDelete && this.isWritePermission) {
      buttons = buttonDelete;
    }
    if (this.isEdit) {
      if (this.isDelete) {
        buttons = buttons + '&nbsp;';
      }
      buttons = buttons + buttonDetail;
    }

    // busca si ya hay definido algun botón mas
    if (appTableOptionsAux.columnDefs !== undefined && appTableOptionsAux.columnDefs !== null) {
      for (const colAux of appTableOptionsAux.columnDefs) {
        // por cada definición de columna
        if (colIndex === colAux.targets) {
          // si encuentra la columna
          if (colAux.defaultContent !== undefined && colAux.defaultContent !== null) {
            colAux.defaultContent = `${colAux.defaultContent as string}&nbsp;${buttons}`;
          } else {
            colAux.defaultContent = buttons;
          }
          return;
        }
      }
    } else {
      appTableOptionsAux.columnDefs = [];
    }

    // si no se ha encontrado la columna
    const col = {
      // width: widthColumn,
      targets: colIndex,
      data: null,
      defaultContent: buttons,
      orderable: false
    };
    appTableOptionsAux.columnDefs.push(col);
  }


  // -------------------------------------------------------------------------
  // metodo para llamar al metodo de acción o borrado al pulsar un botón o icono dentro de la tabla
  private actionEvent(event: any): boolean {

    console.log('click button');

    // cuando se produce el evento
    // this.disabledClick = true;

    // capturo el registro
    const row = this.table.row(event.currentTarget.parentElement.parentElement);

    // capturo el tipo de la primera clase del botón
    const type = event.currentTarget.classList[0];
    const typeParent = event.currentTarget.parentNode.classList[0];
    let isAction = false;
    let isParentAction = false;

    if (type && type === 'APP-ACTION') {
      isAction = true;
    } else {
      if (typeParent && typeParent === 'APP-ACTION') {
        isParentAction = true;
      }
    }

    if (isParentAction || isAction ) {

      // capturo la accion de la segunda clase del botón
      let action = null;
      if (isParentAction) {
        action = event.currentTarget.parentNode.classList[1];
      } else {
        action = event.currentTarget.classList[1];
      }

      if (action !== undefined && action !== null) {

        let entity: EntityInterface;
        if (this.isAngular) {
          entity = JSON.parse(row.data()[0]);
        } else {
          entity = row.data();
        }
        // creo el evento a devolver
        const eventAux = new AppTableEvent();
        eventAux.entity = entity;       // datos del registro
        eventAux.event = action;          // tipo de evento a devolver
        eventAux.colIndex = 0;            // indice de la columna
        eventAux.rowIndex = row.index();  // indice de la columna

        if (action === AppTableEvent.EVENT_REMOVE && this.isMethodEnabled(this.appDeleteMethod)) {
          if (this.isWritePermission) {
            this.setSelectRow(row, false);
            this.appDeleteMethod.next(eventAux);
          }
        } else if (action === AppTableEvent.EVENT_EDIT && this.isMethodEnabled(this.appEditMethod)) {
          this.setSelectRow(row, false);
          this.appEditMethod.next(eventAux);
        } else {
          if (this.isMethodEnabled(this.appActionMethod)) {
            this.setSelectRow(row, false);
            this.appActionMethod.next(eventAux);
          }
        }
      }
    }

    return false;

  }


  // -------------------------------------------------------------------------
  // función para inicializar los listener de la tabla
  private setListeners() {

    // ...................................................................
    // Eventos para los botones internos de la tabla, y acciones .........
    if (
      this.isMethodEnabled(this.appDeleteMethod) || this.isMethodEnabled(this.appEditMethod) ||
      this.isMethodEnabled(this.appActionMethod)) {
      this.table.on('click', 'button', (event: any) => this.actionEvent(event));
    }

    // ...................................................................
    // Eventos para el CLICK .............................................
    if (this.isMethodEnabled(this.appClickMethod) || this.appTableOptions.select) {
      // si tiene metodo de retorno para el click o la tabla tiene la opción de
      // selección
      // se define la escucha al evento
      this.table.on('click', 'td', (event: any) => {

        console.log('click');

        // cuando se produce el evento

        // pregunta si tiene que desactivar el evento de click, porque
        // proceda de un enter
        if (this.disabledClick) {
          // desactiva el evento
          this.disabledClick = false;
          return false;
        }

        // capturo el registro
        const row = this.table.row(event.currentTarget.parentElement);

        // pregunta si no tiene pulsado el ctrl o el sifht, o que
        // no esté en opción selección
        if ((!event.ctrlKey && !event.shiftKey) || !this.appTableOptions.select ) {
          // si es un click solo sin ctrl ni shift
          if (this.isMethodEnabled(this.appClickMethod)) {
            // si tiene evento de retorno de click

            // capturo el indice
            const colIndex = event.currentTarget.cellIndex;

            let entity: EntityInterface;
            if (this.isAngular) {
              entity = JSON.parse(row.data()[0]);
            } else {
              entity = row.data();
            }

            // creo el evento a devolver
            const eventAux = new AppTableEvent();
            eventAux.entity = entity;                 // datos del registro
            eventAux.event = AppTableEvent.EVENT_ENTER; // tipo de evento a devolver
            eventAux.colIndex = colIndex;               // indice de la columna
            eventAux.rowIndex = row.index();            // indice de la fila
            const multipleSelecteion = this.appTableOptions.multipleSelected;
            eventAux.multipleSelected = multipleSelecteion !== undefined ? multipleSelecteion : false;
                // se llama al metodo de retorno con el evento y los datos
            this.appClickMethod.next(eventAux);
          }
        }

        this.setSelectRow(row, event.ctrlKey, event.shiftKey);

        const cell = this.table.cell(event.currentTarget);
        cell.focus();

        return false;

      });
    }

    // ...................................................................
    // eventos para las teclas (ENTER, o CTRL+F, o CTRL+B) ...............
    if (this.isMethodEnabled(this.appShiftEnterMethod) ||
        this.isMethodEnabled(this.appEnterMethod) ||
        this.appTableOptions.select ||
        this.isCopyEnable ||
        this.isPasteEnable ||
        this.isSearchTableInput) {
      // si tiene metodo de retorno para el click o la tabla tiene la opción de
      // selección
      this.table.on('keydown', 'td', (event: any) => {

        if (event.key === 'Enter') {
          // si es la tecla enter

          // capturo el registro
          const row = this.table.row(event.currentTarget.parentElement);

          if (!event.ctrlKey && (this.isMethodEnabled(this.appEnterMethod) || this.isMethodEnabled(this.appShiftEnterMethod))) {
            // si no está pulsado ctrl y además está definido un evento
            // de retorno de enter o shiftEnter

            if (event.shiftKey && this.isMethodEnabled(this.appShiftEnterMethod)) {
              // si es el evento shift enter
              // llamo al metodo de retorno enviando un array
              // de todos los objetos seleccionados

              // capturo los registros seleccionados
              const selectedRows = this.table.rows( { selected: true } );

              // inicializo el objeto de evento con los datos json
              const eventAux = new AppTableEvent();
              eventAux.entity = selectedRows.data();     // datos del registro
              eventAux.event = AppTableEvent.EVENT_ENTER; // tipo de evento a devolver
              eventAux.colIndex = 0;                      // indice de la columna
              eventAux.rowIndex = row.index();            // indice de la fila
                // llamo al metodo de retorno
              // this.appShiftEnterMethod.next(eventAux);

            } else if (this.isMethodEnabled(this.appEnterMethod)) {
              // si es enter normal y existe el evento de enter

              // capturo el indice
              const colIndex = event.currentTarget.cellIndex;

              let entity: EntityInterface;
              if (this.isAngular) {
                entity = JSON.parse(row.data()[0]);
              } else {
                entity = row.data();
              }

              // creo el evento a devolver
              const eventAux = new AppTableEvent();
              eventAux.entity = entity;                 // datos del registro
              eventAux.event = AppTableEvent.EVENT_ENTER; // tipo de evento a devolver
              eventAux.colIndex = colIndex;               // indice de la columna
              eventAux.rowIndex = row.index();            // indice de la fila
                    // se llama al metodo de retorno con el evento y los datos
              this.appEnterMethod.next(eventAux);
            }
          }

          // preguntamos si está habilitada la opción de selección
          if (this.appTableOptions.select) {
            // se deshabilita el que se pulse el click cuando se ha pulsado el enter
            this.disabledClick = true;

            this.setSelectRow(row, event.ctrlKey);

          }
          // elimina cualquier otro evento producido por enter
          return false;

        } else if ((event.key === 'c' || event.key === 'C') &&
                    event.ctrlKey &&
                    this.isCopyEnable) {
          this.copySelectedRows();
          // elimina cualquier otro evento producido por copy
          return false;
        } else if ((event.key === 'v' || event.key === 'V') &&
                    event.ctrlKey &&
                    this.isPasteEnable) {
          this.paste();
          // elimina cualquier otro evento producido por copy
          return false;
        } else if ((event.key === 'c' || event.key === 'C') &&
                    event.ctrlKey &&
                    this.isCopyEnable) {
          this.copySelectedRows();
          // elimina cualquier otro evento producido por copy
          return false;
        } else if ((event.key === 'b' || event.key === 'B') &&
                    event.ctrlKey &&
                    this.isSearchTableInput) {
          event.preventDefault();
          this.searchTableInput.nativeElement.focus();
        }
      });
    }

    // ..................................................................
    // eventos para el doble click ......................................
    if (this.isMethodEnabled(this.appDblClickMethod)) {
      this.table.on('dblclick', 'td', (event: any) => {
        console.log('dblclick');
        const colIndex = event.currentTarget.cellIndex;
        const eventAux = new AppTableEvent();
        const row = this.table.row(event.currentTarget.parentElement);

        let entity: EntityInterface;
        if (this.isAngular) {
          entity = JSON.parse(row.data()[0]);
        } else {
          entity = row.data();
        }

        eventAux.entity = entity;
        eventAux.event = AppTableEvent.EVENT_DBLCLICK;
        eventAux.colIndex = colIndex;               // indice de la columna
        eventAux.rowIndex = row.index();            // indice de la fila

        this.setSelectRow(row, false);

        this.appDblClickMethod.next(eventAux);
      });
    }

    // ...................................................................
    // definimos el listener para el cambio de celda por pulsar la tecla
    if (this.appTableOptions.keys) {
      this.table.on('key-focus', (e: any, datatable: any, cell: any) => {
        // cuando se detecta el cambio de celda con las teclas
        // ponemos el foco en la celda seleccionada
        cell.node().focus();
      });
    }

  }

  private setSelectRow(row: any, ctrlKey: boolean, shiftKey = false) {
    this.appTableOptions.multipleSelected = this.appTableOptions.multipleSelected !== undefined ? this.appTableOptions.multipleSelected : false;
    if (this.appTableOptions.select) {
      // La tabla tiene seleccion
        const index = row.index();
        if (ctrlKey) {
          // si tiene control
          if (!this.isSelected(row) && this.appTableOptions.multipleSelected) {
            // si el actual no seleccionado se selecciona
            row.select();
            this.lastSelectedIndex = index;
          } else {
            // si el actual seleccionado se de-selecciona
            row.deselect();
          }
        } else if (shiftKey) {
          // si tiene mayusculas

          if (!this.appTableOptions.multipleSelected) {
            // Deseleccionamos todas las filas
            if (this.isSelected(row)) {
              this.table.rows().deselect();
            } else {
              this.table.rows().deselect();
              row.select();
              this.lastSelectedIndex = index;
            }

          } else {

            if (this.lastSelectedIndex === null) {
              row.select();
            } else {
              const fromIndex = this.lastSelectedIndex <= index ? this.lastSelectedIndex : index;
              const toIndex = this.lastSelectedIndex <= index ? index : this.lastSelectedIndex;
              for (let i = fromIndex; i <= toIndex; i++) {
                const rowAux = this.table.row(i);
                rowAux.select();
              }
              this.lastSelectedIndex = index;
            }
          }


        } else {
          if (!this.appTableOptions.multipleSelected) {
            // Deseleccionamos todas las filas
            if (this.isSelected(row)) {
              this.table.rows().deselect();
            } else {
              this.table.rows().deselect();
              row.select();
              this.lastSelectedIndex = index;
            }

          } else {
            // si no es con cntrl se deshabilita antes todos los registro seleccionados
            if (this.isSelected(row)) {
              this.table.rows().deselect();
            } else {
              this.table.rows().deselect();
              row.select();
              this.lastSelectedIndex = index;
            }
          }
        }
      }

  }

  private isSelected(row: any) {
    const selectedRows = this.table.rows( { selected: true } );
    let isFound = false;
    selectedRows.every(( index: number) => {
      if (row.index() === index) {
        isFound = true;
        return;
      }
    });
    return isFound;
 }

  // private setDataResize(): void {

  //   // inicializamos la altura del título
  //   this.headHeight = this.getHeadHeight();

  //   // preguntamos si no es de scroll y si de páginas
  //   if (!this.isScroll) {
  //     // si es con página
  //     // capturamos la altura de los registros
  //     this.rowHeight = this.getRowHeight();
  //     // capturamos la altura del control de páginas
  //     this.pageControllerHeight = this.getControllerHeight();
  //   }

  // }

  // -------------------------------------------------------------------------
  // captura la altura del primer registro de la tabla
  private getRowHeight(): number {

    const height: number = this.element.nativeElement.rows[1].offsetHeight;
    return (height === 0 ? 49 : height);
  }

  // -------------------------------------------------------------------------
  // captura el valor la altura de la cabecera de la tabla
  private getHeadHeight(): number {
    // TODO: por ahora el valor es fijo, esto tendrá que cambiar
    const height: number = this.element.nativeElement.childNodes[0].offsetHeight;
    return (height === 0 ? 51 : height);
  }

  // ---------------------------------------i----------------------------------
  // captura el valor de la altura del control de página
  private getControllerHeight(): number {
    // TODO: por ahora el valor es fijo, esto tendrá que cambiar
    return 54;
  }

  // // -------------------------------------------------------------------------
  // // redimensionar la altura de la tabla para adaparta al contenedor
  // private resize(
  //       // // si está cargando
  //       // isInitialize: boolean,
  //       // si se obliga a hacer el resizeado
  //       isMustResize: boolean): void {

  //   // Comprobamos si el tamaño del contenedor de la tabla está definido
  //   if (this.parentDivHeight === 0) {
  //     // si no está definido lo definimos con lo que capturamos
  //     this.parentDivHeight = this.parentElement.offsetHeight;
  //   }

  //   // inicializamos la nueva altura del contenedor a 0
  //   let newParentContentHeight = 0;
  //   // capturamos la nueva altura del div contenedor
  //   newParentContentHeight = this.parentElement.offsetHeight;

  //   // comrpobamos si se ha marcado como obligatorio el redimensionar el temaño y comparamos
  //   // el nuevo tamaño del contenedor con el anterior
  //   if (isMustResize || newParentContentHeight !== this.parentDivHeight) {
  //     // si se ha marcado como obligatorio el redimensionar
  //     // o si la nueva altura es distita de la anterior altura

  //     // cambiamos la variable de la altura del div contenedor a la nueva
  //     this.parentDivHeight = newParentContentHeight;

  //     // se pregunta si no es una tabla con scroll y es paginado
  //     if (!this.isScroll) {
  //       // si no es con Scroll

  //       // capturamos el número de registros mostrado
  //       const recordsDisplay = this.table.page.info().recordsDisplay;
  //       if (recordsDisplay > 1) {
  //         // si hay más de un registro

  //         // calculamos la nueva altura de la tabla
  //         // altura del div contenedor - altura del título - altura del controlador de página
  //         const modificateHeight = newParentContentHeight - this.headHeight - this.pageControllerHeight;
  //         // capturamos la altura de los registros
  //         this.rowHeight = this.getRowHeight();
  //         // capturamos los registros que caben en dicha altura:
  //         // el valor absoluto (truncado) de (la altura / la altura por registro)
  //         const newRowsPerPage = Math.floor(modificateHeight / this.rowHeight);

  //         // comrpobamos si el número de registro es dintinto del anterior que se tenía
  //         if (newRowsPerPage !== this.rowPerPage) {
  //           // si es distinto, se redimensiona la tabla
  //           console.log('resize');
  //           // cambiamos el número de registros por página
  //           $(this.element.nativeElement).DataTable().page.len(newRowsPerPage);
  //           // se repinta la tabla
  //           $(this.element.nativeElement).DataTable().draw();
  //         }
  //       }

  //     } else {
  //       // si es de tipo de scroll

  //       // calculamos la nueva altura del panel de scroll de la tabla
  //       // le cambiamos la altura al panel de scroll de la tabla
  //       $(this.element.nativeElement.parentElement).height(newParentContentHeight - this.headHeight - 10);
  //     }
  //   }
  // }


}
